/**
 * Asset endpoints — object search, asset listing, and the two multipart
 * upload routes the chat surface uses.
 *
 * The JSON GETs (`/objects/search`, `/assets`) route through the typed
 * `http.*` client so they inherit auth-refresh + 401 retry (#3268).
 *
 * The two uploads (`POST /assets/upload`, `POST /workspaces/:id/import/upload`)
 * send `multipart/form-data`. `http.*` always JSON-stringifies its body and
 * sets `Content-Type: application/json`, which would corrupt a multipart
 * request, so those two stay on a raw `fetch()` exactly like the sibling
 * multipart wrappers in endpoints/objects.thumbnails.ts. They live here (not
 * in a scanned feature dir) so the raw-fetch guard's feature/module ceilings
 * still trend to zero while behavior is preserved byte-for-byte.
 *
 * Hand-typed shapes: `xavior-openapi.yaml` has no generated types for these
 * routes (same precedent as endpoints/objects.thumbnails.ts /
 * endpoints/email-module.ts).
 */
import { env } from '@/lib/env'
import { http } from '@/lib/http'
import { getAuthItem } from '@/lib/authStorage'

/* ------------------------------------------------------------------ *
 * JSON reads (via http.*)
 * ------------------------------------------------------------------ */

export interface AssetSearchHit {
  id: string
  title?: string
  attributes?: { filename?: string; [key: string]: unknown }
  [key: string]: unknown
}

interface ObjectSearchResponse {
  items?: AssetSearchHit[]
  results?: AssetSearchHit[]
}

/**
 * `GET /objects/search?q=…&type=…&limit=…` — search recent objects.
 * Returns the raw envelope so callers can read either `items` or `results`.
 */
export async function searchObjects(
  params: { q: string; type?: string; limit?: number },
  authToken?: string | null,
): Promise<ObjectSearchResponse> {
  const searchParams: Record<string, string> = { q: params.q }
  if (params.type) searchParams.type = params.type
  if (typeof params.limit === 'number') searchParams.limit = String(params.limit)
  const res = await http.get<ObjectSearchResponse>('/objects/search', { authToken, searchParams })
  return res.data
}

interface AssetsListResponse {
  items?: AssetSearchHit[]
}

/** `GET /assets?limit=…` — list recent assets. */
export async function listAssets(
  params: { limit?: number },
  authToken?: string | null,
): Promise<AssetsListResponse> {
  const searchParams: Record<string, string> = {}
  if (typeof params.limit === 'number') searchParams.limit = String(params.limit)
  const res = await http.get<AssetsListResponse>('/assets', { authToken, searchParams })
  return res.data
}

/* ------------------------------------------------------------------ *
 * Multipart uploads (raw fetch — FormData can't go through http.*)
 * ------------------------------------------------------------------ */

export interface UploadedAsset {
  id: string
  attributes: { storageUrl?: string }
}

/**
 * Thrown by {@link uploadAssets} when the server returns 413 with a
 * `STORAGE_LIMIT_EXCEEDED` body, so the caller can surface the quota UI
 * without re-parsing the response.
 */
export class StorageLimitExceededError extends Error {
  readonly storageLimitExceeded = true
  constructor(message = 'STORAGE_LIMIT_EXCEEDED') {
    super(message)
    this.name = 'StorageLimitExceededError'
  }
}

/**
 * `POST /assets/upload` — multipart upload of one or more files as workspace
 * assets. Mirrors the raw-fetch call site it replaces:
 *   - the gateway resolves workspace context from `X-Workspace-Id` (the
 *     multipart body is parsed after the per-user-db middleware), so the
 *     header is required to avoid a 400 WORKSPACE_CONTEXT_REQUIRED,
 *   - `conversationId` is optional — the server deprecated it as an upload
 *     scope (#1843/ADR-032: assets are workspace-level); chat still passes it
 *     for the legacy best-effort routing, workspace-level callers (e.g. the
 *     brand-kit Image upload, #5364) omit it,
 *   - on 413 + `STORAGE_LIMIT_EXCEEDED` it throws {@link StorageLimitExceededError},
 *   - any other non-2xx resolves to `[]` (logged), matching prior behavior.
 */
export async function uploadAssets(input: {
  files: File[]
  conversationId?: string
  token: string | null
  workspaceId?: string
  defaultModuleId?: string
}): Promise<UploadedAsset[]> {
  const formData = new FormData()
  if (input.conversationId) formData.append('conversationId', input.conversationId)
  if (input.workspaceId) formData.append('workspaceId', input.workspaceId)
  if (input.defaultModuleId) {
    formData.append('moduleLinks', JSON.stringify([{ moduleId: input.defaultModuleId }]))
  }
  input.files.forEach((f) => formData.append('files', f))

  const headers: Record<string, string> = {}
  if (input.token) headers['Authorization'] = `Bearer ${input.token}`
  const storedEmail = getAuthItem('userEmail')
  if (storedEmail) headers['X-User-Email'] = storedEmail
  if (input.workspaceId) headers['X-Workspace-Id'] = input.workspaceId

  const res = await fetch(`${env.apiBaseUrl}/assets/upload`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  })
  if (!res.ok) {
    // A Response body can only be read once. Read it as text first, then parse
    // JSON from the string so a non-STORAGE_LIMIT_EXCEEDED 413 doesn't fall
    // through to a second (failing) read that masks the real error in the log.
    const bodyText = await res.text().catch(() => '')
    if (res.status === 413) {
      let parsed: { error?: string } = {}
      try {
        parsed = bodyText ? (JSON.parse(bodyText) as { error?: string }) : {}
      } catch {
        // Non-JSON 413 body — fall through to the generic error log below.
      }
      if (parsed.error === 'STORAGE_LIMIT_EXCEEDED') {
        throw new StorageLimitExceededError()
      }
    }
    console.error('[uploadAssets] Upload failed:', res.status, bodyText)
    return []
  }
  const data = await res.json()
  return (data.files as UploadedAsset[]) || []
}

export interface ImportUploadFile {
  id: string
  filename: string
  status: string
  format?: string
  objectType?: string
  recordCount?: number
  error?: string | null
}

export interface ImportUploadResponse {
  sessionId: string
  files?: ImportUploadFile[]
  totalRecords?: number
}

/**
 * `POST /workspaces/:workspaceId/import/upload` — multipart upload of import
 * files. Throws on a non-2xx response with the same `Upload failed: <status>
 * - <text>` message shape the raw call site produced.
 */
export async function uploadImportFiles(input: {
  workspaceId: string
  conversationId: string
  bodyWorkspaceId: string
  files: File[]
  token: string | null
}): Promise<ImportUploadResponse> {
  const formData = new FormData()
  formData.append('conversationId', input.conversationId)
  formData.append('workspaceId', input.bodyWorkspaceId)
  input.files.forEach((file) => formData.append('files', file))

  const response = await fetch(
    `${env.apiBaseUrl}/workspaces/${input.workspaceId}/import/upload`,
    {
      method: 'POST',
      headers: input.token ? { Authorization: `Bearer ${input.token}` } : {},
      body: formData,
      credentials: 'include',
    },
  )
  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Upload failed: ${response.status} - ${errorText}`)
  }
  return (await response.json()) as ImportUploadResponse
}

export const assets = {
  searchObjects,
  listAssets,
  uploadAssets,
  uploadImportFiles,
}
